import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { RadarTab, MatrixViewMode, PeriodPreset, DirectReportSortBy, SortDirection, SavedEmployee } from '@/types/radar'

/**
 * Lightweight employee reference for recents/favorites
 * Stores just enough to display in search without refetching
 */
export interface RecentEmployee {
  employeeId: string
  name: string
  pictureUrl?: string | null
}

/**
 * Radar UI State
 *
 * Manages ONLY client-side UI state for the Radar de Desempenho module.
 * Server data is handled by TanStack Query hooks.
 *
 * This store contains:
 * - Tab navigation (individual, leader, matrix)
 * - Search modal state
 * - Period filter state
 * - Comparison mode state
 * - Matrix view configuration
 * - Direct reports sorting
 *
 * DO NOT store server data here (radar results, employee data, etc.)
 */
interface RadarUIStore {
  // ================================
  // TAB NAVIGATION
  // ================================

  /** Current active tab */
  activeTab: RadarTab

  /** Set active tab */
  setActiveTab: (tab: RadarTab) => void

  // ================================
  // SEARCH STATE
  // ================================

  /** Is search modal open */
  isSearchOpen: boolean

  /** Open search modal */
  openSearch: () => void

  /** Close search modal */
  closeSearch: () => void

  /** Toggle search modal */
  toggleSearch: () => void

  /** Recent searches (max 5) - stores employee info for display */
  recentSearches: RecentEmployee[]

  /** Add a search to recent searches */
  addRecentSearch: (employee: RecentEmployee) => void

  /** Clear recent searches */
  clearRecentSearches: () => void

  /** Favorite employees - stores employee info for display */
  favorites: RecentEmployee[]

  /** Toggle favorite */
  toggleFavorite: (employee: RecentEmployee) => void

  /** Check if employee is a favorite */
  isFavorite: (employeeId: string) => boolean

  // ================================
  // PERIOD FILTER
  // ================================

  /** Period preset selection */
  periodPreset: PeriodPreset

  /** Custom period range */
  customPeriod: { startDate: string; endDate: string } | null

  /** Set period preset */
  setPeriodPreset: (preset: PeriodPreset) => void

  /** Set custom period */
  setCustomPeriod: (period: { startDate: string; endDate: string }) => void

  /** Get current period dates */
  getCurrentPeriod: () => { startDate: string; endDate: string }

  // ================================
  // COMPARISON MODE
  // ================================

  /** Comparison target employee ID */
  compareEmployeeId: string | null

  /** Set comparison employee */
  setCompareEmployee: (id: string | null) => void

  /** Is comparison modal open */
  isCompareModalOpen: boolean

  /** Open comparison modal */
  openCompareModal: () => void

  /** Close comparison modal */
  closeCompareModal: () => void

  // ================================
  // MATRIX VIEW
  // ================================

  /** Matrix view mode (summary or detailed) */
  matrixViewMode: MatrixViewMode

  /** Set matrix view mode */
  setMatrixViewMode: (mode: MatrixViewMode) => void

  /** Expanded pillars in matrix view */
  expandedPillars: string[]

  /** Toggle pillar expansion */
  togglePillar: (pillar: string) => void

  /** Expand all pillars */
  expandAllPillars: () => void

  /** Collapse all pillars */
  collapseAllPillars: () => void

  // ================================
  // DIRECT REPORTS SORTING
  // ================================

  /** Sort by field */
  directReportsSortBy: DirectReportSortBy

  /** Sort direction */
  directReportsSortDir: SortDirection

  /** Set sort configuration */
  setDirectReportsSort: (sortBy: DirectReportSortBy, direction?: SortDirection) => void

  // ================================
  // UTILITY
  // ================================

  /** Reset all UI state */
  reset: () => void
}

const initialState = {
  activeTab: 'individual' as RadarTab,
  isSearchOpen: false,
  recentSearches: [] as RecentEmployee[],
  favorites: [] as RecentEmployee[],
  periodPreset: '90d' as PeriodPreset,
  customPeriod: null as { startDate: string; endDate: string } | null,
  compareEmployeeId: null as string | null,
  isCompareModalOpen: false,
  matrixViewMode: 'summary' as MatrixViewMode,
  expandedPillars: [] as string[],
  directReportsSortBy: 'score' as DirectReportSortBy,
  directReportsSortDir: 'desc' as SortDirection,
}

/**
 * Calculate period dates from preset
 */
function calculatePeriodFromPreset(preset: PeriodPreset): { startDate: string; endDate: string } {
  const endDate = new Date()
  const startDate = new Date()

  switch (preset) {
    case '30d':
      startDate.setDate(startDate.getDate() - 30)
      break
    case '60d':
      startDate.setDate(startDate.getDate() - 60)
      break
    case '90d':
    default:
      startDate.setDate(startDate.getDate() - 90)
      break
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  }
}

/**
 * Radar de Desempenho UI Store
 *
 * Persisted to localStorage so preferences survive page refreshes.
 * Modal state is NOT persisted (resets on refresh).
 */
export const useRadarUIStore = create<RadarUIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Tab navigation
      setActiveTab: (tab) => set({ activeTab: tab }),

      // Search state
      openSearch: () => set({ isSearchOpen: true }),
      closeSearch: () => set({ isSearchOpen: false }),
      toggleSearch: () => set((state) => ({ isSearchOpen: !state.isSearchOpen })),

      addRecentSearch: (employee) => set((state) => {
        const filtered = state.recentSearches.filter((e) => e.employeeId !== employee.employeeId)
        const updated = [employee, ...filtered].slice(0, 5)
        return { recentSearches: updated }
      }),

      clearRecentSearches: () => set({ recentSearches: [] }),

      toggleFavorite: (employee) => set((state) => {
        const isFav = state.favorites.some((e) => e.employeeId === employee.employeeId)
        if (isFav) {
          return { favorites: state.favorites.filter((e) => e.employeeId !== employee.employeeId) }
        }
        return { favorites: [...state.favorites, employee] }
      }),

      isFavorite: (employeeId) => get().favorites.some((e) => e.employeeId === employeeId),

      // Period filter
      setPeriodPreset: (preset) => set({ periodPreset: preset, customPeriod: null }),

      setCustomPeriod: (period) => set({
        periodPreset: 'custom',
        customPeriod: period
      }),

      getCurrentPeriod: () => {
        const state = get()
        if (state.periodPreset === 'custom' && state.customPeriod) {
          return state.customPeriod
        }
        return calculatePeriodFromPreset(state.periodPreset)
      },

      // Comparison mode
      setCompareEmployee: (id) => set({ compareEmployeeId: id }),
      openCompareModal: () => set({ isCompareModalOpen: true }),
      closeCompareModal: () => set({ isCompareModalOpen: false }),

      // Matrix view
      setMatrixViewMode: (mode) => set({ matrixViewMode: mode }),

      togglePillar: (pillar) => set((state) => {
        const isExpanded = state.expandedPillars.includes(pillar)
        if (isExpanded) {
          return { expandedPillars: state.expandedPillars.filter((p) => p !== pillar) }
        }
        return { expandedPillars: [...state.expandedPillars, pillar] }
      }),

      expandAllPillars: () => set({ expandedPillars: ['lideranca', 'execucao', 'colaboracao', 'valores'] }),

      collapseAllPillars: () => set({ expandedPillars: [] }),

      // Direct reports sorting
      setDirectReportsSort: (sortBy, direction) => set((state) => {
        const newDirection = direction ?? (state.directReportsSortBy === sortBy
          ? (state.directReportsSortDir === 'asc' ? 'desc' : 'asc')
          : 'desc')
        return {
          directReportsSortBy: sortBy,
          directReportsSortDir: newDirection
        }
      }),

      // Utility
      reset: () => set(initialState),
    }),
    {
      name: 'radar-ui-store', // localStorage key
      // Only persist preferences (not modal state)
      partialize: (state) => ({
        activeTab: state.activeTab,
        recentSearches: state.recentSearches,
        favorites: state.favorites,
        periodPreset: state.periodPreset,
        customPeriod: state.customPeriod,
        matrixViewMode: state.matrixViewMode,
        directReportsSortBy: state.directReportsSortBy,
        directReportsSortDir: state.directReportsSortDir,
      }),
    }
  )
)
